import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import api, { extractError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ticket, setTicket] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.needs_2fa) {
        setTicket(data.ticket);
        toast.info("2FA کوڈ درج کریں");
      } else {
        if (data.token) localStorage.setItem("paknet_token", data.token);
        await refresh();
        toast.success("خوش آمدید");
        nav("/");
      }
    } catch (err) { toast.error(extractError(err)); }
    finally { setLoading(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/2fa", { ticket, code });
      if (data.token) localStorage.setItem("paknet_token", data.token);
      await refresh();
      toast.success("خوش آمدید");
      nav("/");
    } catch (err) { toast.error(extractError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto py-10" data-testid="login-page">
      <Card className="p-6 sm:p-8 space-y-5">
        <div className="text-center space-y-1">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-eng">
            <Sparkles size={12} /> PakNet
          </div>
          <h1 className="urdu-naskh text-3xl font-semibold text-primary">{t.login}</h1>
        </div>
        {!ticket ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="urdu-naskh" htmlFor="email">{t.email}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="urdu-naskh" htmlFor="pw">{t.password}</Label>
              <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary" data-testid="login-submit">
              {loading ? t.loading : t.login}
            </Button>
            <div className="flex justify-between text-sm">
              <Link to="/forgot-password" className="text-primary underline urdu-naskh" data-testid="forgot-link">پاس ورڈ بھول گئے؟</Link>
              <Link to="/register" className="text-primary underline urdu-naskh">{t.register}</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submit2fa} className="space-y-4">
            <div className="text-center flex items-center gap-2 justify-center">
              <ShieldCheck className="text-primary" size={22} />
              <p className="urdu-naskh">اپنی موبائل ایپ سے ٦ ہندسوں کا کوڈ درج کریں</p>
            </div>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              dir="ltr"
              className="text-center text-2xl tracking-widest font-eng"
              maxLength={6}
              autoFocus
              data-testid="login-2fa-code"
            />
            <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-primary" data-testid="login-2fa-submit">
              {loading ? t.loading : "تصدیق کریں"}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => { setTicket(null); setCode(""); }}>واپس</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
